/**
 * Activity (Phase 8, admins only): who did what, and when, from the audit log.
 * The log holds actions, names of things and counts, never what was typed
 * into a deed (no names of clients, no ID numbers).
 *
 * Owner 10-21 (item 5): a second tab, Reviews, lists the executives' review
 * requests (template_reviews through /api/reviews, not the audit log); Reply
 * and Mark done go through audited routes.
 */
import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { z } from 'zod';
import { actionsInGroup, AUDIT_GROUP_LABEL, AUDIT_GROUPS, auditLabel, type AuditGroup } from '@saledeed/shared';
import { api } from '../api/client';
import type { AuditEntry, AuditPage, ProjectSummary, User } from '../api/types';
import { Badge, Button, ErrorBox, Field, formatDateTime, Select, Spinner, TableCard, TextInput } from '../components/ui';
import { ReviewsTab } from '../reviews/ReviewsTab';
import { useReviewCounts } from '../reviews/ReviewTag';

const ROLE: Record<string, string> = {
  admin: 'admin', manager: 'manager', executive_view: 'executive', executive_editor: 'executive-editor',
  executive: 'executive', bootstrap: 'set-up', share_link: 'someone with a share link',
};
const GROUP_TONE: Record<AuditGroup, 'slate' | 'green' | 'amber' | 'red' | 'blue'> = {
  signin: 'slate', people: 'amber', templates: 'blue', projects: 'green', documents: 'blue', reviews: 'amber', share: 'amber', other: 'slate',
};
const WARN = new Set(['auth.login_failed', 'auth.refresh_reuse_detected', 'user.reset', 'project.delete', 'document.discard', 'share.revoke']);
const DETAIL_KEY: Record<string, string> = {
  kind: 'kind', labels: 'labels', label_count: 'labels', fields: 'changed', templateCode: 'template', version: 'version',
  revision: 'revision', userCode: 'person', role: 'role', exec_subtype: 'access', status: 'status', field_count: 'fields',
  block_count: 'blocks', members: 'members', templates: 'templates', expiresInDays: 'days', maxDownloads: 'download limit',
  bytes: 'bytes', wordChanged: 'wording changed', wordingChanged: 'wording changed', title: 'title',
};

/** Details as short "key: value" pairs. Long lists and hashes are shortened. */
export function detailText(detail: Record<string, unknown> | null): string {
  if (!detail) return '';
  return Object.entries(detail)
    .filter(([k]) => k !== 'replaces')
    .map(([k, v]) => {
      const value = Array.isArray(v) ? (v.length > 4 ? `${v.length} items` : v.join(', '))
        : typeof v === 'string' && /^[0-9a-f]{40,}$/.test(v) ? `${v.slice(0, 10)}…`
          : v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      return `${DETAIL_KEY[k] ?? k}: ${value}`;
    })
    .join(' · ');
}

const groupOf = (action: string): AuditGroup => (AUDIT_GROUPS.find(g => actionsInGroup(g).includes(action)) ?? 'other');

/** "2026-10-09" (this computer's day) -> the moment that day starts, as ISO. */
const dayStart = (d: string, plusDays = 0) => { const t = new Date(`${d}T00:00:00`); t.setDate(t.getDate() + plusDays); return t.toISOString(); };

interface Filter { group: '' | AuditGroup; action: string; actor: string; projectId: string; from: string; to: string }

export function toQuery(f: Filter, before?: number): string {
  const q = new URLSearchParams();
  if (f.action) q.set('action', f.action); else if (f.group) q.set('group', f.group);
  if (f.actor) q.set('actor', f.actor);
  if (f.projectId) q.set('projectId', f.projectId);
  if (f.from) q.set('from', dayStart(f.from));
  if (f.to) q.set('to', dayStart(f.to, 1));
  if (before) q.set('before', String(before));
  q.set('limit', '50');
  return q.toString();
}

function csvOf(rows: AuditEntry[]): string {
  const cell = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const head = ['When', 'Who', 'Code', 'Role', 'What', 'Thing', 'Project', 'Details', 'Place'];
  const lines = rows.map(e => [
    new Date(e.at).toLocaleString('en-IN'), e.actor?.name ?? '', e.actor?.code ?? '', e.actor?.role ? ROLE[e.actor.role] ?? e.actor.role : '',
    auditLabel(e.action), e.entity ? `${e.entity.type}${e.entity.name ? `: ${e.entity.name}` : ''}` : '', e.project?.name ?? '', detailText(e.detail), e.place ?? '',
  ].map(v => cell(String(v))).join(','));
  return [head.join(','), ...lines].join('\r\n');
}

function download(rows: AuditEntry[]) {
  const url = URL.createObjectURL(new Blob(['\ufeff', csvOf(rows)], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * `?tab=reviews&templateCode=T-…` (the "N reviews" badge links here). Read like `actor`/`projectId` below and
 * checked here: a bad value falls back to the All tab / no template filter.
 */
export const ActivitySearch = z.object({
  tab: z.enum(['all', 'reviews']).catch('all'),
  templateCode: z.string().trim().toUpperCase().regex(/^T-[0-9A-Z]{4,16}$/).optional().catch(undefined),
});
export type ActivityTab = z.infer<typeof ActivitySearch>['tab'];

export function readActivitySearch(search: string) {
  const q = new URLSearchParams(search);
  return ActivitySearch.parse({ tab: q.get('tab') ?? undefined, templateCode: q.get('templateCode') ?? undefined });
}

export function ActivityPage() {
  const [search, setSearch] = useState(() => readActivitySearch(location.search));
  const counts = useReviewCounts();
  const open = Object.values(counts.data ?? {}).reduce((n, c) => n + c.open, 0);
  const show = (tab: ActivityTab, templateCode?: string) => {
    setSearch({ tab, templateCode });
    const q = new URLSearchParams(location.search);
    q.delete('tab'); q.delete('templateCode');
    if (tab === 'reviews') { q.set('tab', 'reviews'); if (templateCode) q.set('templateCode', templateCode); }
    const rest = q.toString();
    history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}`);
  };
  const tabs: { id: ActivityTab; label: string }[] = [{ id: 'all', label: 'All activity' }, { id: 'reviews', label: `Reviews (${open} open)` }];

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4 p-4 sm:p-6" data-testid="activity">
      <div>
        <h1 className="text-xl font-semibold">Activity</h1>
        <div role="tablist" aria-label="Activity" className="mt-2 flex gap-1 border-b border-slate-200">
          {tabs.map(t => (
            <button key={t.id} type="button" role="tab" id={`activity-tab-${t.id}`} aria-selected={search.tab === t.id}
              aria-controls={`activity-panel-${t.id}`} onClick={() => show(t.id)}
              className={`-mb-px rounded-t border px-3 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-blue-600 ${search.tab === t.id ? 'border-slate-200 border-b-white bg-white font-medium' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div role="tabpanel" id={`activity-panel-${search.tab}`} aria-labelledby={`activity-tab-${search.tab}`} className="flex flex-col gap-4">
        {search.tab === 'reviews'
          ? <ReviewsTab templateCode={search.templateCode} onClearTemplate={() => show('reviews')} />
          : <AuditList />}
      </div>
    </div>
  );
}

/** Tab "All activity": the read-only audit list. */
function AuditList() {
  const initial = new URLSearchParams(location.search);
  const [filter, setFilter] = useState<Filter>({
    group: '', action: '', actor: initial.get('actor') ?? '', projectId: initial.get('projectId') ?? '', from: '', to: '',
  });
  const set = (patch: Partial<Filter>) => setFilter(f => ({ ...f, ...patch }));
  const people = useQuery({ queryKey: ['users'], queryFn: () => api<{ users: User[] }>('GET', '/api/admin/users') });
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api<{ projects: ProjectSummary[] }>('GET', '/api/projects') });
  const log = useInfiniteQuery({
    queryKey: ['audit', filter],
    queryFn: ({ pageParam }) => api<AuditPage>('GET', `/api/audit?${toQuery(filter, pageParam)}`),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: last => last.next ?? undefined,
  });
  const rows = useMemo(() => log.data?.pages.flatMap(p => p.entries) ?? [], [log.data]);
  const actions = filter.group ? actionsInGroup(filter.group) : [];
  const filtered = Object.values(filter).some(Boolean);

  return (
    <>
      <div>
        <p className="text-sm text-slate-600">
          Who did what, and when. It shows actions and the names of templates, projects and people; never what was typed into a deed
          (no client names, no Aadhaar or PAN). Nothing here can be changed or deleted.
        </p>
      </div>

      <form className="flex flex-wrap items-end gap-3" onSubmit={e => e.preventDefault()} aria-label="Filter the activity">
        <Field label="Area">{id => (
          <Select id={id} value={filter.group} onChange={e => set({ group: e.target.value as Filter['group'], action: '' })}>
            <option value="">Everything</option>
            {AUDIT_GROUPS.map(g => <option key={g} value={g}>{AUDIT_GROUP_LABEL[g]}</option>)}
          </Select>
        )}</Field>
        {filter.group && (
          <Field label="What">{id => (
            <Select id={id} value={filter.action} onChange={e => set({ action: e.target.value })}>
              <option value="">All of {AUDIT_GROUP_LABEL[filter.group as AuditGroup].toLowerCase()}</option>
              {actions.map(a => <option key={a} value={a}>{auditLabel(a)}</option>)}
            </Select>
          )}</Field>
        )}
        <Field label="Person">{id => (
          <Select id={id} value={filter.actor} onChange={e => set({ actor: e.target.value })}>
            <option value="">Anyone</option>
            {people.data?.users.map(u => <option key={u.id} value={u.userCode}>{u.displayName} · {u.userCode}</option>)}
          </Select>
        )}</Field>
        <Field label="Project">{id => (
          <Select id={id} value={filter.projectId} onChange={e => set({ projectId: e.target.value })}>
            <option value="">Any project</option>
            {projects.data?.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        )}</Field>
        <Field label="From">{id => <TextInput id={id} type="date" value={filter.from} onChange={e => set({ from: e.target.value })} />}</Field>
        <Field label="To">{id => <TextInput id={id} type="date" value={filter.to} onChange={e => set({ to: e.target.value })} />}</Field>
        {filtered && <Button tone="ghost" onClick={() => setFilter({ group: '', action: '', actor: '', projectId: '', from: '', to: '' })}>Clear filters</Button>}
        <span className="grow" />
        <Button disabled={!rows.length} onClick={() => download(rows)} title="The entries shown below, as a spreadsheet file">Download as CSV ({rows.length})</Button>
      </form>

      <ErrorBox error={log.error} />
      {log.isPending ? <Spinner label="Loading activity" /> : (
        <TableCard>
          <caption className="sr-only">Activity, newest first</caption>
          <thead>
            <tr><th scope="col">When</th><th scope="col">Who</th><th scope="col">What</th><th scope="col">On</th><th scope="col">Details</th></tr>
          </thead>
          <tbody>
            {rows.map(e => (
              <tr key={e.id} data-action={e.action}>
                <td className="whitespace-nowrap text-xs">{formatDateTime(e.at)}</td>
                <td className="text-sm">
                  {e.actor?.name ?? (e.actor?.role ? ROLE[e.actor.role] ?? e.actor.role : <span className="text-slate-500">not signed in</span>)}
                  <span className="block text-xs text-slate-500">
                    {[e.actor?.code, e.actor?.name && e.actor.role ? ROLE[e.actor.role] ?? e.actor.role : null].filter(Boolean).join(' · ')}
                    {e.place && <span title="Same tag = same place (network address, stored scrambled)"> · place {e.place}</span>}
                  </span>
                </td>
                <td className="text-sm">
                  <Badge tone={WARN.has(e.action) ? 'red' : GROUP_TONE[groupOf(e.action)]}>{AUDIT_GROUP_LABEL[groupOf(e.action)]}</Badge>{' '}
                  {auditLabel(e.action)}
                </td>
                <td className="text-sm">
                  {e.entity?.name ? (e.entity.type === 'template' && e.entity.id
                    ? <Link to="/templates/$id" params={{ id: e.entity.id }} className="text-blue-800 underline">{e.entity.name}</Link>
                    : e.entity.name) : e.entity ? <span className="text-slate-500">{e.entity.type.replace('_', ' ')}{e.entity.type === 'project' || e.entity.type === 'template' ? ' (deleted)' : ''}</span> : ''}
                  {e.project && e.entity?.type !== 'project' && (
                    <span className="block text-xs text-slate-500">
                      in {e.project.name ? <Link to="/projects/$id" params={{ id: e.project.id }} className="underline">{e.project.name}</Link> : 'a deleted project'}
                    </span>
                  )}
                </td>
                <td className="max-w-md text-xs text-slate-600">{detailText(e.detail)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="py-6 text-center text-slate-500">{filtered ? 'Nothing matches these filters.' : 'Nothing recorded yet.'}</td></tr>}
          </tbody>
        </TableCard>
      )}
      {log.hasNextPage && (
        <div><Button onClick={() => void log.fetchNextPage()} disabled={log.isFetchingNextPage}>{log.isFetchingNextPage ? 'Loading…' : 'Show older'}</Button></div>
      )}
    </>
  );
}
